import { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AuthContext } from "../../auth/auth-context";

const TABS = [
  { id: "profile", label: "Profile", icon: "👤" },
  { id: "subscription", label: "Subscription", icon: "💳" },
  { id: "billing", label: "Billing", icon: "🧾" },
];

const SectionCard = ({ title, children }) => (
  <div
    style={{
      background: "var(--white)",
      border: "1px solid var(--border)",
      borderRadius: "12px",
      padding: "22px",
      marginBottom: "14px",
      boxShadow: "var(--shadow)",
    }}
  >
    <div
      style={{
        fontSize: "14px",
        fontWeight: 700,
        marginBottom: "16px",
        paddingBottom: "12px",
        borderBottom: "1px solid var(--border)",
        color: "var(--ink)",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const FieldRow = ({ label, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "130px 1fr",
      gap: "8px",
      alignItems: "center",
      marginBottom: "12px",
    }}
  >
    <span
      style={{ fontSize: "12px", fontWeight: 600, color: "var(--ink-muted)" }}
    >
      {label}
    </span>
    <span style={{ fontSize: "13px", color: "var(--ink)" }}>{children}</span>
  </div>
);

const GhostBtn = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: "5px 10px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: 600,
      background: "none",
      border: "1px solid var(--border)",
      color: "var(--ink-soft)",
      cursor: "pointer",
      fontFamily: "inherit",
    }}
  >
    {children}
  </button>
);

const Account = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("profile");

  const isSubscribed = user?.stripeSubscriptionIsActive;
  const isTrialActive = user?.isTrialActive;
  const trialDaysRemaining = user?.trialDaysRemaining;

  return (
    <div
      style={{
        background: "var(--cream)",
        minHeight: "100vh",
        padding: "28px 28px 72px",
      }}
    >
      <Helmet>
        <title>My Account | tastebud.ee</title>
        <meta name='robots' content='noindex' />
        <link rel='canonical' href='https://tastebud.ee/account/my-account' />
      </Helmet>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        <h1
          style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "22px",
            marginBottom: "4px",
            color: "var(--ink)",
          }}
        >
          My Account
        </h1>
        <p
          style={{
            fontSize: "13px",
            color: "var(--ink-muted)",
            marginBottom: "24px",
          }}
        >
          Manage your profile, subscription, and billing.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "190px 1fr",
            gap: "20px",
            alignItems: "start",
          }}
        >
          {/* Sidebar */}
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: "12px",
              padding: "8px",
              boxShadow: "var(--shadow)",
            }}
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  borderRadius: "7px",
                  fontSize: "13px",
                  fontWeight: 500,
                  width: "100%",
                  background:
                    activeTab === tab.id ? "var(--brand-light)" : "none",
                  color:
                    activeTab === tab.id ? "var(--brand)" : "var(--ink-soft)",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  transition: "all 0.12s",
                }}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Content panels */}
          <div>
            {activeTab === "profile" && (
              <SectionCard title="Profile Details">
                <FieldRow label="Name">{user?.name || "—"}</FieldRow>
                <FieldRow label="Email">{user?.email || "—"}</FieldRow>
                <FieldRow label="Password">
                  <span style={{ color: "var(--ink-muted)" }}>••••••••</span>
                </FieldRow>
                <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                  <GhostBtn>Edit Profile</GhostBtn>
                  <GhostBtn>Change Password</GhostBtn>
                </div>
              </SectionCard>
            )}

            {(activeTab === "profile" || activeTab === "subscription") && (
              <SectionCard title="Subscription">
                <FieldRow label="Current plan">
                  {isSubscribed && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 10px",
                        background: "#DCFCE7",
                        border: "1px solid #BBF7D0",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#15803D",
                      }}
                    >
                      ✓ Active subscription
                    </span>
                  )}
                  {!isSubscribed && isTrialActive && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 10px",
                        background: "#FEF3C7",
                        border: "1px solid #FDE68A",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#92400E",
                      }}
                    >
                      ⏳ Free Trial — {trialDaysRemaining} days left
                    </span>
                  )}
                  {!isSubscribed && !isTrialActive && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "4px 10px",
                        background: "#FEE2E2",
                        border: "1px solid #FECACA",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#991B1B",
                      }}
                    >
                      Trial ended
                    </span>
                  )}
                </FieldRow>

                {isTrialActive && !isSubscribed && (
                  <FieldRow label="Trial includes">
                    <span
                      style={{
                        fontSize: "12px",
                        color: "var(--ink-muted)",
                      }}
                    >
                      1 business · 1 menu · 1 location
                    </span>
                  </FieldRow>
                )}

                {isSubscribed && user?.stripeSubscription?.productName && (
                  <FieldRow label="Plan">
                    {user.stripeSubscription.productName}
                  </FieldRow>
                )}

                <div style={{ marginTop: "16px" }}>
                  {!isSubscribed ? (
                    <Link
                      to="/account/subscription"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        background: "var(--brand)",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      Upgrade to Standard Plan — £5/mo
                    </Link>
                  ) : (
                    <Link
                      to="/account/subscription"
                      style={{
                        display: "inline-flex",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        background: "none",
                        border: "1px solid var(--border)",
                        color: "var(--ink-soft)",
                        textDecoration: "none",
                      }}
                    >
                      Manage subscription
                    </Link>
                  )}
                </div>
              </SectionCard>
            )}

            {activeTab === "billing" && (
              <SectionCard title="Billing">
                <p
                  style={{
                    fontSize: "13px",
                    color: "var(--ink-muted)",
                    lineHeight: "1.6",
                  }}
                >
                  Billing history and invoice management is available through
                  the Stripe customer portal.
                </p>
                <div style={{ marginTop: "16px" }}>
                  <Link
                    to="/account/subscription"
                    style={{
                      display: "inline-flex",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: 600,
                      background: "none",
                      border: "1px solid var(--border)",
                      color: "var(--ink-soft)",
                      textDecoration: "none",
                    }}
                  >
                    Open billing portal
                  </Link>
                </div>
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
